// src/components/walkingTour/WalkingTourView.tsx
//
// Renders the top-half map + waypoint controls for a walking_tour
// conversation. The chat itself (bottom half) is rendered by the
// caller — this component just wraps it.
//
// Layout:
//   ┌─────────────────────────┐
//   │      MAP (50vh)         │  ← user dot, all waypoints, route to current
//   │  [pin] [pin] [pin] ...  │
//   ├─────────────────────────┤
//   │ Waypoint 3/7 — 280m walk│  ← header strip + I've arrived button
//   ├─────────────────────────┤
//   │       (chat)            │  ← rendered by parent
//   └─────────────────────────┘

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Drawer,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircleOutlined,
  CheckCircle,
  RadioButtonUncheckedOutlined,
  SkipNextOutlined,
  CloseOutlined,
  DirectionsWalkOutlined,
  PlaceOutlined,
  CloseFullscreenOutlined,
  MapOutlined,
} from '@mui/icons-material';
import {
  Map as GMap,
  AdvancedMarker,
  useMap,
  useApiIsLoaded,
} from '@vis.gl/react-google-maps';
import { useLocation } from '../../hooks/useLocation';
import { useWalkingRoute } from '../../hooks/useWalkingRoute';
import {
  useTourLegs,
  formatLegDistance,
  formatLegDuration,
} from '../../hooks/useTourLegs';
import type { TourLeg } from '../../hooks/useTourLegs';
import { UserLocationMarker } from '../map/UserLocationMarker';
import { Conversation } from '../../stores/conversationStore';
import type {
  WalkingTourWaypoint,
  WalkingTourWaypointStatus,
} from '../../stores/conversationStore';
import { doc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';
import { useT } from '../../i18n/useT';

interface Props {
  conversation: Conversation;
  /** Optional callback to send a message into the chat from the
   *  view. Used for the turn-taking signals: "I've arrived at X.",
   *  "Continue to the next stop.", "We've finished the tour." When
   *  omitted, the buttons fall back to just persisting state changes
   *  with no chat signal. */
  onSendMessage?: (message: string) => void;
}

/** Great-circle distance in meters. Used to decide "is the user
 *  close enough to the current waypoint that we should suggest they
 *  tap I've arrived?" — independent from the walking-route distance,
 *  which can be larger if the path snakes around. */
function haversineM(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371000; // earth radius in meters
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Color for each waypoint status. */
function colorFor(status: WalkingTourWaypointStatus): {
  fill: string;
  ring: string;
} {
  switch (status) {
    case 'visited':
      return { fill: '#16a34a', ring: 'white' }; // green
    case 'current':
      return { fill: '#f59e0b', ring: 'white' }; // amber, pulsing
    case 'skipped':
      return { fill: '#9ca3af', ring: 'white' }; // gray
    case 'pending':
    default:
      return { fill: '#ea4335', ring: 'white' }; // red
  }
}

const WaypointPin: React.FC<{
  index: number;
  status: WalkingTourWaypointStatus;
}> = ({ index, status }) => {
  const c = colorFor(status);
  return (
    <Box
      sx={{
        width: status === 'current' ? 32 : 26,
        height: status === 'current' ? 32 : 26,
        borderRadius: '50%',
        bgcolor: c.fill,
        border: `3px solid ${c.ring}`,
        boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
        color: 'white',
        fontSize: 12,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        ...(status === 'current'
          ? {
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -8,
                borderRadius: '50%',
                bgcolor: 'rgba(245, 158, 11, 0.35)',
                animation: 'wpPulse 2s ease-out infinite',
              },
              '@keyframes wpPulse': {
                '0%': { transform: 'scale(0.7)', opacity: 0.8 },
                '100%': { transform: 'scale(1.6)', opacity: 0 },
              },
            }
          : {}),
      }}
    >
      {index + 1}
    </Box>
  );
};

const UserDot: React.FC = () => <UserLocationMarker size={14} />;

/** Draws polyline from user → current waypoint. Lives inside <Map>.
 *  Polyline points are computed once in the parent (useWalkingRoute)
 *  and passed in, so the same data feeds both the route line AND
 *  the smart bounds fitter without duplicate API calls. */
const RouteOverlay: React.FC<{
  polyline: { lat: number; lng: number }[] | null;
}> = ({ polyline }) => {
  const map = useMap();
  const isLoaded = useApiIsLoaded();
  const polylineRef = useRef<google.maps.Polyline | null>(null);

  React.useEffect(() => {
    if (!map || !isLoaded) return;
    if (!polyline || polyline.length === 0) {
      polylineRef.current?.setMap(null);
      polylineRef.current = null;
      return;
    }
    if (!polylineRef.current) {
      polylineRef.current = new google.maps.Polyline({
        strokeOpacity: 0,
        icons: [
          {
            icon: {
              path: 'M 0,-1 0,1',
              strokeOpacity: 1,
              strokeColor: '#1a73e8',
              strokeWeight: 5,
              scale: 4,
            },
            offset: '0',
            repeat: '14px',
          },
        ],
      });
      polylineRef.current.setMap(map);
    }
    polylineRef.current.setPath(polyline);
  }, [map, isLoaded, polyline]);

  React.useEffect(() => {
    return () => {
      polylineRef.current?.setMap(null);
      polylineRef.current = null;
    };
  }, []);

  return null;
};

/** Auto-fits bounds based on tour state:
 *  - In progress (waypoints + user known): fit user + current
 *    waypoint + the walking-route polyline points. Tight,
 *    actionable — "here's where you are, here's where you're
 *    headed, here's the path." Updates as the user walks.
 *  - Tour complete: fit ALL waypoints + user as an overview.
 *  - No user location: fit current + nearby waypoints so the user
 *    at least sees the tour.
 *
 *  Refits whenever a meaningful input changes (user position
 *  rounded to 4 decimals — about 11m, finer than waypoint
 *  granularity). */
const TourBoundsFitter: React.FC<{
  user: { lat: number; lng: number } | null;
  waypoints: WalkingTourWaypoint[];
  currentIndex: number;
  isComplete: boolean;
  /** Polyline points from useWalkingRoute (user → current
   *  waypoint). When present, included in the fit so the route
   *  line isn't clipped at the edges. */
  polyline: { lat: number; lng: number }[] | null;
}> = ({ user, waypoints, currentIndex, isComplete, polyline }) => {
  const map = useMap();
  const isLoaded = useApiIsLoaded();
  const fittedFor = useRef<string | null>(null);

  React.useEffect(() => {
    if (!map || !isLoaded || waypoints.length === 0) return;

    // Cache key — bumps the fit when any of these change. User
    // coords rounded to 4 decimal places (≈11m) so the map doesn't
    // re-fit on every GPS jitter.
    const userKey = user
      ? `${user.lat.toFixed(4)},${user.lng.toFixed(4)}`
      : 'no-user';
    const polylineLen = polyline?.length ?? 0;
    const key = `${currentIndex}|${isComplete}|${userKey}|${polylineLen}`;
    if (fittedFor.current === key) return;

    const bounds = new google.maps.LatLngBounds();

    if (isComplete) {
      // Overview mode: show the full tour. Useful for reflection,
      // sharing, or just looking at the whole loop.
      if (user) bounds.extend(user);
      for (const wp of waypoints) {
        bounds.extend({ lat: wp.lat, lng: wp.lng });
      }
    } else {
      // Smart-leg mode: just user + current destination + the
      // route between them. The user doesn't need to see future
      // stops while they're walking — that's the picker's job.
      const cur = waypoints[currentIndex];
      if (cur) bounds.extend({ lat: cur.lat, lng: cur.lng });
      if (user) bounds.extend(user);
      // Include polyline points if we have them — they may
      // detour around obstacles (rivers, one-ways) and would
      // otherwise be clipped at the bounding box edges.
      if (polyline && polyline.length > 0) {
        for (const p of polyline) {
          bounds.extend(p);
        }
      }
      // No user location yet? Add the next 1-2 waypoints so the
      // user can at least see what's ahead.
      if (!user) {
        const next1 = waypoints[currentIndex + 1];
        const next2 = waypoints[currentIndex + 2];
        if (next1) bounds.extend({ lat: next1.lat, lng: next1.lng });
        if (next2) bounds.extend({ lat: next2.lat, lng: next2.lng });
      }
    }

    // Generous padding so pins don't sit at the edge of the map.
    // 80px is enough for the ~32px markers + a little breathing
    // room on both sides.
    map.fitBounds(bounds, { top: 80, right: 60, bottom: 80, left: 60 });
    fittedFor.current = key;
  }, [
    map,
    isLoaded,
    waypoints,
    currentIndex,
    isComplete,
    user?.lat,
    user?.lng,
    polyline,
  ]);

  return null;
};

export const WalkingTourView: React.FC<Props> = ({ conversation, onSendMessage }) => {
  const t = useT();
  const wt = conversation.walking_tour;
  const { location } = useLocation();
  const user =
    location?.latitude && location?.longitude
      ? { lat: location.latitude, lng: location.longitude }
      : null;

  const [pickerWaypointIndex, setPickerWaypointIndex] = useState<number | null>(
    null
  );
  const [updating, setUpdating] = useState(false);

  // Minimize/expand toggle for the top-half map. When minimized the
  // map collapses to a small floating circle in the top-left corner
  // of the chat area, freeing the full screen for chat. Click the
  // circle to expand. State is per-mount; resets on conversation
  // change naturally because the component remounts.
  const [minimized, setMinimized] = useState(false);

  // ─── Draggable map height ────────────────────────────────────
  //
  // The map+stops-header form a single positioned panel where the
  // header acts as the drag handle. Three snap points:
  //
  //   peek      — handle just visible, map mostly hidden behind it.
  //               Used when the user wants to focus on chat.
  //   default   — ~50vh map. The current/legacy default.
  //   expanded  — ~80vh map. For close-up of pins, route, etc.
  //
  // We track height in vh units (relative to the viewport) so the
  // panel feels right on phones of different sizes. The actual
  // pixel height is computed from window.innerHeight at gesture
  // time so resize/orientation work without re-snapping.
  //
  // During a drag, transitions are disabled so motion is 1:1 with
  // the finger. After release we re-enable and snap to the nearest
  // detent. A small "free drag" tolerance prevents accidental snaps
  // when the user just nudges by a few px.
  const SNAP_PEEK_VH = 8;     // ~handle height + a sliver of map
  const SNAP_DEFAULT_VH = 50; // current default
  const SNAP_EXPANDED_VH = 80;
  type SnapPoint = 'peek' | 'default' | 'expanded';
  const [snapPoint, setSnapPoint] = useState<SnapPoint>('default');
  const [mapHeightVh, setMapHeightVh] = useState<number>(SNAP_DEFAULT_VH);
  const [dragging, setDragging] = useState(false);
  // Live drag delta state, kept in refs so handlers don't recreate on
  // every move. We only commit to setMapHeightVh on each pointermove
  // (cheap — sets transform via React, browser's compositor handles
  // the rest).
  const dragStartYRef = useRef<number | null>(null);
  const dragStartVhRef = useRef<number>(SNAP_DEFAULT_VH);

  const heightVhFromSnap = useCallback((s: SnapPoint): number => {
    return s === 'peek'
      ? SNAP_PEEK_VH
      : s === 'expanded'
        ? SNAP_EXPANDED_VH
        : SNAP_DEFAULT_VH;
  }, []);

  const nearestSnap = useCallback((vh: number): SnapPoint => {
    // Distances to each detent. Pick the nearest. Equal-weight
    // — no momentum or velocity tracking yet (keep it simple;
    // ergonomic enough for 3 snaps).
    const dPeek = Math.abs(vh - SNAP_PEEK_VH);
    const dDefault = Math.abs(vh - SNAP_DEFAULT_VH);
    const dExpanded = Math.abs(vh - SNAP_EXPANDED_VH);
    const min = Math.min(dPeek, dDefault, dExpanded);
    if (min === dPeek) return 'peek';
    if (min === dExpanded) return 'expanded';
    return 'default';
  }, []);

  const handleDragPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Only start a drag for primary button / single touch. Right-
      // click and multi-touch (pinch zoom on the handle) are ignored.
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      // Don't start a drag if the user is pressing on something
      // interactive — buttons, icon buttons, etc. We walk up the
      // DOM looking for any role/tag that would normally consume
      // a click, and bail if we find one. This means the bar stays
      // draggable in the white space between buttons but doesn't
      // hijack taps on "I've arrived" or the picker icon.
      const target = e.target as HTMLElement;
      const interactive = target.closest(
        'button, a, [role="button"], input, [data-no-drag]',
      );
      if (interactive) return;
      // Capture the pointer so subsequent pointermove/up still hit
      // us even if the finger drifts off the handle (over the map).
      target.setPointerCapture(e.pointerId);
      dragStartYRef.current = e.clientY;
      dragStartVhRef.current = mapHeightVh;
      setDragging(true);
    },
    [mapHeightVh],
  );

  const handleDragPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (dragStartYRef.current === null) return;
      const deltaPx = e.clientY - dragStartYRef.current;
      const vhPerPx = 100 / window.innerHeight;
      const proposed = dragStartVhRef.current + deltaPx * vhPerPx;
      // Clamp inside [peek, expanded] — going below peek hides the
      // handle entirely (bad), above expanded fights with the
      // bottom nav (also bad).
      const clamped = Math.max(
        SNAP_PEEK_VH,
        Math.min(SNAP_EXPANDED_VH, proposed),
      );
      setMapHeightVh(clamped);
    },
    [],
  );

  const handleDragPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (dragStartYRef.current === null) return;
      try {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        // Capture release can fail if the target was already released
        // (e.g. unmounted mid-drag). Safe to ignore.
      }
      // If the user barely moved (< 4px), treat it as a tap rather
      // than a drag. Tap on the handle cycles through snaps:
      // default → peek → expanded → default. (Skipping the
      // current state on tap-cycle would be slightly nicer but
      // adds complexity; this is fine.)
      const dy = Math.abs(e.clientY - dragStartYRef.current);
      let target: SnapPoint;
      if (dy < 4) {
        target =
          snapPoint === 'default'
            ? 'peek'
            : snapPoint === 'peek'
              ? 'expanded'
              : 'default';
      } else {
        target = nearestSnap(mapHeightVh);
      }
      setSnapPoint(target);
      setMapHeightVh(heightVhFromSnap(target));
      dragStartYRef.current = null;
      setDragging(false);
    },
    [mapHeightVh, snapPoint, nearestSnap, heightVhFromSnap],
  );

  // Keep mapHeightVh synced when snapPoint changes via other paths
  // (e.g. minimize FAB exits then re-mounts at default). Cheap.
  useEffect(() => {
    if (!dragging) {
      setMapHeightVh(heightVhFromSnap(snapPoint));
    }
  }, [snapPoint, dragging, heightVhFromSnap]);
  // ────────────────────────────────────────────────────────────


  const waypoints = wt?.waypoints || [];
  const currentIndex =
    typeof wt?.current_waypoint_index === 'number'
      ? wt.current_waypoint_index
      : 0;
  const currentWaypoint = waypoints[currentIndex] || null;

  // Whole-tour leg distances + total. One Directions API call per
  // tour load, returns N-1 legs and the cumulative total. Hook
  // returns null while loading or on failure — UI hides the leg
  // metrics gracefully in that case. Hook is keyed on the joined
  // coords so it doesn't refetch on every parent re-render.
  const tourLegs = useTourLegs(
    waypoints.map((w) => ({ lat: w.lat, lng: w.lng })),
  );

  // Walking-route metadata for the header AND for both the route
  // overlay polyline + the smart bounds fitter. Single hook call —
  // route data is shared so we don't trigger the same fetch twice.
  const { distanceM, durationS, polyline: routePolyline } = useWalkingRoute(
    currentWaypoint
      ? { lat: currentWaypoint.lat, lng: currentWaypoint.lng }
      : null,
    user
  );

  // Crow-flies distance from user to current waypoint. We use this
  // for the arrival hint rather than the walking-route distance,
  // because once a user is *near* the spot, GPS proximity matters
  // more than path length (which can be inflated by one-way streets,
  // dead ends, etc.). 30m chosen as the trigger threshold — close
  // enough to plausibly be there, loose enough to allow GPS jitter.
  const ARRIVAL_HINT_M = 30;
  const directDistanceM = useMemo(() => {
    if (!user || !currentWaypoint) return null;
    return haversineM(
      user.lat,
      user.lng,
      currentWaypoint.lat,
      currentWaypoint.lng
    );
  }, [user, currentWaypoint]);
  const isNearCurrent =
    !wt?.completed_at &&
    directDistanceM !== null &&
    directDistanceM <= ARRIVAL_HINT_M;

  const visitedCount = useMemo(
    () => waypoints.filter((w) => w.status === 'visited').length,
    [waypoints]
  );

  const mapId =
    (import.meta as any).env?.VITE_GOOGLE_MAPS_MAP_ID || undefined;

  // ── Mutations ──

  /**
   * Update one or more waypoints' status (and optionally
   * arrived_at), the current_waypoint_index pointer, and/or the
   * tour's completed_at marker — atomically in one Firestore write.
   *
   * IMPORTANT (bug-fix): we MUST replace the entire `waypoints`
   * array. Firestore's dot-notation for nested fields does NOT
   * support array element access — writing
   *   `walking_tour.waypoints.0.status = 'visited'`
   * silently converts the array into a map keyed by string indices
   * (`{0: {…}, 1: {…}}`). On the next snapshot the conversation-
   * store mapper's `Array.isArray(wt.waypoints)` check returns
   * false and the mapper returns an empty waypoints list — which
   * looks to the user like the map "lost all the destinations"
   * after a single I've-arrived tap.
   *
   * The fix is to read the current waypoints from React state,
   * apply the patches in memory, and write the whole array back
   * under a single field path. Firestore handles array writes
   * fine — it's only the dot-path-into-array that's broken.
   */
  const persistWaypointChange = useCallback(
    async (
      patch: {
        nextIndex?: number;
        statusUpdates?: Array<{ index: number; status: WalkingTourWaypointStatus; arrived_at?: any }>;
        completedAt?: any;
      }
    ) => {
      if (!auth.currentUser || !conversation.id) return;
      setUpdating(true);
      try {
        const updates: Record<string, any> = {};

        if (patch.statusUpdates && patch.statusUpdates.length) {
          // Build a lookup from index → patch so we can apply all
          // changes in one map() pass.
          const patchByIndex = new Map<
            number,
            { status: WalkingTourWaypointStatus; arrived_at?: any }
          >();
          for (const u of patch.statusUpdates) {
            patchByIndex.set(u.index, {
              status: u.status,
              arrived_at: u.arrived_at,
            });
          }
          const newWaypoints = waypoints.map((w, i) => {
            const p = patchByIndex.get(i);
            if (!p) return w;
            const merged: any = { ...w, status: p.status };
            // Only overwrite arrived_at when the caller provided
            // a value — preserving any existing timestamp on
            // unrelated patches.
            if (p.arrived_at !== undefined) {
              merged.arrived_at = p.arrived_at;
            }
            return merged;
          });
          updates['walking_tour.waypoints'] = newWaypoints;
        }

        if (typeof patch.nextIndex === 'number') {
          updates['walking_tour.current_waypoint_index'] = patch.nextIndex;
        }
        if (patch.completedAt !== undefined) {
          updates['walking_tour.completed_at'] = patch.completedAt;
        }
        updates['updated_at'] = serverTimestamp();
        await updateDoc(doc(db, 'conversations', conversation.id), updates);
      } catch (err) {
        console.warn('persistWaypointChange failed:', err);
      } finally {
        setUpdating(false);
      }
    },
    [conversation.id, waypoints]
  );

  // ── Turn-taking handlers ────────────────────────────────────────
  // Two-step flow per stop, matching how a real guide works:
  //
  //   1. User walks to stop, taps "I've arrived". handleArrived
  //      marks arrived_at on the current waypoint. status STAYS
  //      'current' — the pointer doesn't advance yet. The synthetic
  //      message tells the AI to deliver the rich on-site content.
  //
  //   2. User has heard the AI's arrival content, taps "Continue".
  //      handleContinue marks the current waypoint 'visited' and
  //      advances current_waypoint_index to the next stop, which is
  //      flipped to 'current'. The synthetic message tells the AI
  //      to give the transition turn.
  //
  //   3. On the LAST stop, "Continue" becomes "Finish tour".
  //      handleContinue marks visited and sets completed_at; the
  //      synthetic message triggers the AI's wrap-up turn.
  const handleArrived = useCallback(async () => {
    if (!currentWaypoint || waypoints.length === 0) return;
    const idx = currentIndex;
    await persistWaypointChange({
      statusUpdates: [
        {
          index: idx,
          // Status stays 'current' — only arrived_at changes.
          // The pointer doesn't advance until the user taps
          // Continue.
          status: 'current',
          arrived_at: Timestamp.now(),
        },
      ],
    });
    onSendMessage?.(`I've arrived at ${currentWaypoint.title}.`);
  }, [currentWaypoint, currentIndex, waypoints, persistWaypointChange, onSendMessage]);

  const handleContinue = useCallback(async () => {
    if (!currentWaypoint || waypoints.length === 0) return;
    const idx = currentIndex;
    const isLast = idx === waypoints.length - 1;
    if (isLast) {
      // Last stop → mark visited + completedAt. No advance.
      await persistWaypointChange({
        statusUpdates: [{ index: idx, status: 'visited' }],
        completedAt: serverTimestamp(),
      });
      onSendMessage?.("We've finished the tour.");
      return;
    }
    // Mid-tour → mark current visited, mark next current, advance.
    const nextIdx = idx + 1;
    await persistWaypointChange({
      nextIndex: nextIdx,
      statusUpdates: [
        { index: idx, status: 'visited' },
        { index: nextIdx, status: 'current' },
      ],
    });
    onSendMessage?.('Continue to the next stop.');
  }, [currentWaypoint, currentIndex, waypoints, persistWaypointChange, onSendMessage]);

  const handleJumpTo = useCallback(
    async (targetIdx: number) => {
      if (targetIdx < 0 || targetIdx >= waypoints.length) return;
      // Mark waypoints between current and target. Forward jump:
      // intermediate become 'skipped'. Backward: leave them alone.
      const statusUpdates: any[] = [];
      if (targetIdx > currentIndex) {
        for (let i = currentIndex; i < targetIdx; i++) {
          if (waypoints[i].status !== 'visited') {
            statusUpdates.push({ index: i, status: 'skipped' });
          }
        }
      }
      // Mark target as current
      statusUpdates.push({ index: targetIdx, status: 'current' });
      await persistWaypointChange({
        nextIndex: targetIdx,
        statusUpdates,
      });
      setPickerWaypointIndex(null);
    },
    [currentIndex, waypoints, persistWaypointChange]
  );

  // ── Render guards ──

  if (!wt) return null;

  // During AI-driven onboarding (state machine asking the user what
  // kind of tour they want), there's no map or waypoint list to
  // show yet — the chat below is hosting an interactive Q&A. Return
  // null and let the message list use the full screen.
  if ((wt as any).status === 'awaiting_focus') {
    return null;
  }

  if (wt.status === 'generating') {
    return (
      <Box
        sx={{
          height: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
          bgcolor: 'action.hover',
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          
          {t("Designing your walking tour…")}
        </Typography>
      </Box>
    );
  }

  if (wt.status === 'error') {
    return (
      <Box
        sx={{
          minHeight: 200,
          p: 3,
          textAlign: 'center',
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
          
          {t("Tour generation failed")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {wt.last_error || t('Unknown error. Try again.')}
        </Typography>
        {/* Direct retry: flip status back to 'generating' with the
            same focus. The trigger effect in ChatInterface releases
            the dedup key on error, so this re-fires the generator.
            Picking a different theme via chat still works too. */}
        {wt.focus && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              void updateDoc(doc(db, 'conversations', conversation.id), {
                'walking_tour.status': 'generating',
                'walking_tour.last_error': '',
              }).catch((err) =>
                console.warn('walking_tour retry write failed:', err)
              );
            }}
          >
            {t('Try again')}
          </Button>
        )}
      </Box>
    );
  }

  const center = wt.center || (waypoints[0]
    ? { lat: waypoints[0].lat, lng: waypoints[0].lng }
    : { lat: 0, lng: 0 });

  // Compact circular FAB shown when the map is minimized. Floats
  // over the chat at the top-left so the user can keep one eye on
  // tour state while reading. Tap to expand back to full view.
  // position:fixed so it doesn't push chat content; offset under
  // the app bar (which lives at the very top of the page).
  if (minimized) {
    return (
      <Box
        component="button"
        onClick={() => setMinimized(false)}
        aria-label={t('Show map')}
        sx={{
          // Circle FAB. 56px is the standard MUI FAB size; tappable
          // on mobile, doesn't crowd the screen.
          position: 'fixed',
          top: 72,
          left: 12,
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: 'none',
          cursor: 'pointer',
          zIndex: 1100,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          boxShadow:
            '0 3px 5px -1px rgba(0,0,0,0.2), 0 6px 10px 0 rgba(0,0,0,0.14), 0 1px 18px 0 rgba(0,0,0,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          flexDirection: 'column',
          fontSize: 11,
          fontWeight: 600,
          // Hover: lift slightly, like a real FAB.
          transition: 'transform 120ms, box-shadow 120ms',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow:
              '0 5px 5px -3px rgba(0,0,0,0.2), 0 8px 10px 1px rgba(0,0,0,0.14), 0 3px 14px 2px rgba(0,0,0,0.12)',
          },
          // Active state for keyboard / tap feedback.
          '&:active': {
            transform: 'translateY(0)',
          },
        }}
      >
        <MapOutlined fontSize="small" />
        <Box component="span" sx={{ fontSize: 10, lineHeight: 1 }}>
          {visitedCount}/{waypoints.length}
        </Box>
      </Box>
    );
  }

  return (
    <>
      {/* Top-half map — height controlled by drag state. Disable
          transitions during drag so motion is 1:1 with the finger;
          re-enable for snap animation on release. The transform
          property would be cheaper but height is what affects the
          chat below — using height keeps the chat reflow correct.

          zIndex 10 puts both the map and the stops-header (below)
          above the floating composer cluster (zIndex 5 in
          ChatInterface). Without this, when the panel is dragged
          fully expanded (~80vh), the composer overlaps the handle
          and the user can't grab it to pull back up. */}
      <Box
        sx={{
          height: `${mapHeightVh}vh`,
          width: '100%',
          position: 'relative',
          zIndex: 10,
          flexShrink: 0,
          bgcolor: 'action.hover',
          transition: dragging ? 'none' : 'height 280ms cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      >
        <GMap
          defaultCenter={center}
          defaultZoom={15}
          mapId={mapId}
          gestureHandling="greedy"
          disableDefaultUI={true}
          clickableIcons={false}
          keyboardShortcuts={false}
          style={{ width: '100%', height: '100%' }}
        >
          <TourBoundsFitter
            user={user}
            waypoints={waypoints}
            currentIndex={currentIndex}
            isComplete={!!wt.completed_at}
            polyline={routePolyline}
          />

          {user && (
            <AdvancedMarker position={user}>
              <UserDot />
            </AdvancedMarker>
          )}

          {waypoints.map((wp) => (
            <AdvancedMarker
              key={wp.index}
              position={{ lat: wp.lat, lng: wp.lng }}
              onClick={() => setPickerWaypointIndex(wp.index)}
            >
              <WaypointPin index={wp.index} status={wp.status} />
            </AdvancedMarker>
          ))}

          {user && currentWaypoint && (
            <RouteOverlay polyline={routePolyline} />
          )}
        </GMap>

        {/* Minimize button — floats top-right of the map. Tap to
            collapse the map down to the circular FAB above so chat
            takes the full screen. */}
        <Tooltip title={t('Minimize map')}>
          <IconButton
            onClick={() => setMinimized(true)}
            size="small"
            aria-label={t('Minimize map')}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <CloseFullscreenOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Header strip below map — also acts as the drag handle for
          map height. Pointer events on this whole bar trigger the
          drag; the grabber pill at the top is the visual affordance.
          Buttons inside still work because pointerdown on a button
          stops propagating naturally (synthetic events bubble, but
          the button's own click handler fires first). The handle
          surface doesn't include the button area visually since the
          drag mostly happens before the user reaches them. */}
      <Box
        onPointerDown={handleDragPointerDown}
        onPointerMove={handleDragPointerMove}
        onPointerUp={handleDragPointerUp}
        onPointerCancel={handleDragPointerUp}
        sx={{
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: isNearCurrent ? 'success.light' : 'background.paper',
          transition: 'background-color 0.4s ease',
          // touchAction: 'none' tells the browser to NOT handle this
          // gesture as a scroll — without it, vertical drags on
          // mobile compete with the page scroll and feel broken.
          // Buttons inside override this back to 'auto' implicitly
          // because their ancestors don't gate pointer events.
          touchAction: 'none',
          // Hint cursor on desktop. Mobile users won't see this.
          cursor: dragging ? 'grabbing' : 'grab',
          position: 'relative',
          // Same zIndex as the map above — keeps the handle tappable
          // when the panel extends past the floating composer
          // (which is zIndex 5 in ChatInterface).
          zIndex: 10,
          // Top padding accommodates the grabber pill so the chip
          // doesn't sit on top of the existing content.
          pt: 1.5,
          pb: 1.25,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          // Disable text selection during drag — feels more native
          // when nothing flashes blue while the user pulls.
          userSelect: dragging ? 'none' : 'auto',
        }}
      >
        {/* Grabber pill — purely visual affordance for the drag. The
            entire bar is the hit target, but the pill tells the user
            "you can pull this." 36×4 with rounded ends is the
            standard iOS/Material bottom-sheet grabber dimension. */}
        <Box
          sx={{
            position: 'absolute',
            top: 6,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: 'action.disabled',
            opacity: dragging ? 0.7 : 0.45,
            transition: 'opacity 150ms',
            pointerEvents: 'none',
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: wt.completed_at
                ? 'success.dark'
                : isNearCurrent
                ? 'success.dark'
                : 'warning.main',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              display: 'block',
              fontSize: '0.65rem',
              lineHeight: 1.2,
            }}
          >
            {wt.completed_at
              ? '🎉 Tour complete'
              : isNearCurrent
              ? "You're here — tap I've arrived"
              : `Stop ${currentIndex + 1} of ${waypoints.length}`}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, lineHeight: 1.25, mt: 0.25 }}
            noWrap
          >
            {currentWaypoint?.title || wt.title || 'Tour'}
          </Typography>
          {currentWaypoint && user && distanceM != null && !wt.completed_at && (
            <Typography
              variant="caption"
              color={isNearCurrent ? 'success.dark' : 'text.secondary'}
              sx={{ display: 'block', mt: 0.25 }}
            >
              <DirectionsWalkOutlined sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
              {durationS != null
                ? `${Math.max(1, Math.round(durationS / 60))} min · `
                : ''}
              {distanceM < 1000
                ? `${Math.round(distanceM)} m away`
                : `${(distanceM / 1000).toFixed(1)} km away`}
            </Typography>
          )}
          {/* Tour-total caption — separate line from "next stop"
              distance so the user can tell them apart at a glance.
              Hidden until the leg-fetch completes (no false zeros)
              and during the in-progress current-stop guidance,
              total stays in scope. */}
          {tourLegs && !wt.completed_at && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25, opacity: 0.85 }}
            >
              {t('Tour total')}: {formatLegDistance(tourLegs.total_distance_m)}
              {tourLegs.total_duration_s > 0
                ? ` · ${formatLegDuration(tourLegs.total_duration_s)}`
                : ''}
            </Typography>
          )}
          {wt.completed_at && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25 }}
            >
              
              {t("You visited")} {visitedCount}  {t("of")} {waypoints.length}  {t("stops.")}
            </Typography>
          )}
        </Box>
        {!wt.completed_at && currentWaypoint && !currentWaypoint.arrived_at && (
          <Button
            variant="contained"
            color={isNearCurrent ? 'success' : 'warning'}
            size="small"
            onClick={handleArrived}
            disabled={updating}
            startIcon={<CheckCircleOutlined />}
            sx={{
              textTransform: 'none',
              flexShrink: 0,
              // Nudge the button when near, so it draws the eye.
              ...(isNearCurrent
                ? {
                    animation: 'arrivedPulse 1.6s ease-in-out infinite',
                    '@keyframes arrivedPulse': {
                      '0%, 100%': {
                        boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.0)',
                      },
                      '50%': {
                        boxShadow: '0 0 0 8px rgba(76, 175, 80, 0.3)',
                      },
                    },
                  }
                : {}),
            }}
          >
            
            {t("I've arrived")}
          </Button>
        )}
        {/* Continue / Finish — visible after the user has tapped
            "I've arrived" (current waypoint has arrived_at set) and
            the tour isn't yet complete. Last stop's button reads
            "Finish tour". */}
        {!wt.completed_at && currentWaypoint && currentWaypoint.arrived_at && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleContinue}
            disabled={updating}
            startIcon={<DirectionsWalkOutlined />}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            {currentIndex === waypoints.length - 1
              ? t('Finish tour')
              : t('Continue')}
          </Button>
        )}
        <Tooltip title={t("View all stops")}>
          <IconButton
            size="small"
            onClick={() => setPickerWaypointIndex(-1)}
          >
            <PlaceOutlined />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Waypoint picker drawer (also used as the tap-pin detail) */}
      <Drawer
        anchor="bottom"
        open={pickerWaypointIndex !== null}
        onClose={() => setPickerWaypointIndex(null)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '70vh',
          },
        }}
      >
        {pickerWaypointIndex !== null && (
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {pickerWaypointIndex >= 0 && waypoints[pickerWaypointIndex]
                  ? waypoints[pickerWaypointIndex].title
                  : 'All stops'}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setPickerWaypointIndex(null)}
              >
                <CloseOutlined fontSize="small" />
              </IconButton>
            </Box>

            {pickerWaypointIndex >= 0 && waypoints[pickerWaypointIndex] ? (
              <WaypointDetail
                waypoint={waypoints[pickerWaypointIndex]}
                isCurrent={pickerWaypointIndex === currentIndex}
                onJumpTo={() => handleJumpTo(pickerWaypointIndex)}
                onClose={() => setPickerWaypointIndex(null)}
                disabled={updating}
              />
            ) : (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {waypoints.map((wp, idx) => (
                  <WaypointRow
                    key={wp.index}
                    waypoint={wp}
                    isCurrent={wp.index === currentIndex}
                    onClick={() => setPickerWaypointIndex(wp.index)}
                    // Leg from waypoint[idx-1] → this waypoint.
                    // First row (idx === 0) has no incoming leg
                    // and won't render a distance.
                    previousLeg={
                      idx > 0 ? tourLegs?.legs[idx - 1] ?? null : null
                    }
                  />
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Drawer>
    </>
  );
};

const WaypointDetail: React.FC<{
  waypoint: WalkingTourWaypoint;
  isCurrent: boolean;
  onJumpTo: () => void;
  onClose: () => void;
  disabled: boolean;
}> = ({ waypoint, isCurrent, onJumpTo, disabled }) => {
  const t = useT();
  return (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Chip
        label={`Stop ${waypoint.index + 1}`}
        size="small"
        color={isCurrent ? 'warning' : 'default'}
        sx={{ height: 22, fontWeight: 700, fontSize: '0.7rem' }}
      />
      <StatusChip status={waypoint.status} />
    </Box>
    {waypoint.description && (
      <Typography variant="body2" sx={{ mb: 1.25, lineHeight: 1.5 }}>
        {waypoint.description}
      </Typography>
    )}
    {waypoint.why_visit && (
      <Box
        sx={{
          bgcolor: 'action.hover',
          borderRadius: 1.5,
          p: 1.25,
          mb: 1.25,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.secondary',
            display: 'block',
            mb: 0.5,
            fontSize: '0.65rem',
          }}
        >
          {t('Why this stop')}
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
          {waypoint.why_visit}
        </Typography>
      </Box>
    )}
    {waypoint.walking_notes && !isCurrent && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
        {t('Getting there:')} {waypoint.walking_notes}
      </Typography>
    )}
    <Divider sx={{ mb: 1.5 }} />
    {!isCurrent && (
      <Button
        variant="contained"
        startIcon={<SkipNextOutlined />}
        onClick={onJumpTo}
        disabled={disabled}
        fullWidth
        sx={{ textTransform: 'none' }}
      >
        {t('Skip to this stop')}
      </Button>
    )}
  </Box>
  );
};

const StatusChip: React.FC<{ status: WalkingTourWaypointStatus }> = ({
  status,
}) => {
  const cfg: Record<
    WalkingTourWaypointStatus,
    { label: string; color: 'default' | 'success' | 'warning' | 'error' }
  > = {
    pending: { label: 'Pending', color: 'default' },
    current: { label: 'You are here', color: 'warning' },
    visited: { label: 'Visited', color: 'success' },
    skipped: { label: 'Skipped', color: 'default' },
  };
  const { label, color } = cfg[status];
  return (
    <Chip
      label={label}
      size="small"
      color={color}
      sx={{ height: 22, fontSize: '0.7rem' }}
    />
  );
};

const WaypointRow: React.FC<{
  waypoint: WalkingTourWaypoint;
  isCurrent: boolean;
  onClick: () => void;
  /** The leg that ARRIVES at this waypoint (i.e. the leg from
   *  waypoint[index - 1] → this waypoint). Null for the first
   *  waypoint (no incoming leg) and during the loading window
   *  before the Directions fetch settles. */
  previousLeg?: TourLeg | null;
}> = ({ waypoint, isCurrent, onClick, previousLeg }) => {
  const c = colorFor(waypoint.status);
  const legDistanceText =
    previousLeg && previousLeg.distance_m !== null
      ? formatLegDistance(previousLeg.distance_m)
      : null;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        px: 1,
        borderRadius: 1.5,
        cursor: 'pointer',
        bgcolor: isCurrent ? 'warning.light' : 'transparent',
        opacity: isCurrent ? 1 : waypoint.status === 'visited' ? 0.7 : 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: c.fill,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: '0.8rem',
          flexShrink: 0,
        }}
      >
        {waypoint.status === 'visited' ? (
          <CheckCircle sx={{ fontSize: 18 }} />
        ) : (
          waypoint.index + 1
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, lineHeight: 1.3 }}
          noWrap
        >
          {waypoint.title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
          >
            {waypoint.status === 'current'
              ? 'You are here'
              : waypoint.status === 'visited'
              ? 'Visited'
              : waypoint.status === 'skipped'
              ? 'Skipped'
              : 'Pending'}
          </Typography>
          {/* Leg distance from previous stop. Skipped on the first
              row (no previous leg) and while the Directions fetch
              is still loading. Subtle styling — secondary info so
              it doesn't compete with the title. */}
          {legDistanceText !== null && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                opacity: 0.85,
              }}
            >
              <DirectionsWalkOutlined sx={{ fontSize: 11 }} />
              {legDistanceText}
            </Typography>
          )}
        </Box>
      </Box>
      {waypoint.status === 'pending' && (
        <RadioButtonUncheckedOutlined fontSize="small" color="disabled" />
      )}
    </Box>
  );
};
